import type { CSSProperties } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import MeridianLockup from "../../components/MeridianLockup";
import { signOut } from "../../lib/auth";

/**
 * MER-193: layout for every `/admin/*` route. Same structural shape
 * as `DashboardShell` (sticky sidebar + section column) so the admin
 * surface reads as part of the same product, with a "Staff" pill in
 * the sidebar header so it is visually distinct from a customer's
 * dashboard. Children of MER-185 replace AdminPlaceholder per
 * sub-route with the real page.
 */

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/admin", label: "Overview", end: true },
  { to: "/admin/customers", label: "Customers" },
  { to: "/admin/usage", label: "Usage" },
  { to: "/admin/runs", label: "Runs" },
  { to: "/admin/audit", label: "Audit" },
  { to: "/admin/help-desk", label: "Help desk" },
  { to: "/admin/activity", label: "Activity" },
];

const SECTION_HEADER_STYLE: CSSProperties = {
  padding: "8px 16px",
  fontSize: 10,
  letterSpacing: 1,
  textTransform: "uppercase",
  color: "var(--text-muted)",
  fontWeight: 700,
};

function navLinkStyle({ isActive }: { isActive: boolean }): CSSProperties {
  return {
    padding: "9px 16px",
    fontSize: 13,
    color: "var(--text)",
    fontWeight: isActive ? 600 : 400,
    background: isActive
      ? "linear-gradient(90deg, rgba(38,168,159,0.10) 0%, transparent 100%)"
      : "transparent",
    borderLeft: isActive ? "3px solid var(--brand-teal)" : "3px solid transparent",
    textDecoration: "none",
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 8,
  };
}

export default function AdminShell() {
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", minHeight: "100vh" }}>
      <aside
        style={{
          background: "var(--panel)",
          borderRight: "1px solid var(--border)",
          padding: "16px 0",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          position: "sticky",
          top: 0,
          height: "100vh",
          overflowY: "auto",
        }}
      >
        <header style={{ padding: "0 16px" }}>
          <MeridianLockup size="sm" align="row" />
          <div
            style={{
              marginTop: 16,
              fontWeight: 700,
              fontSize: 16,
              color: "var(--text)",
              letterSpacing: -0.2,
            }}
          >
            Admin Console
          </div>
          <div
            style={{
              marginTop: 8,
              display: "inline-block",
              fontSize: 10,
              padding: "4px 10px",
              borderRadius: 12,
              background:
                "linear-gradient(95deg, rgba(15,107,92,0.08) 0%, rgba(38,168,159,0.08) 100%)",
              border: "1px solid var(--brand-line)",
              color: "var(--brand-deep)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              fontWeight: 700,
            }}
          >
            Staff
          </div>
        </header>

        {/* alignContent: "start" because grid's default stretch spreads the
            few admin nav rows across the full leftover viewport height
            (MER-476). DashboardShell has enough rows to mask the same
            default; keep this if rows are ever removed there too. */}
        <nav
          style={{
            display: "grid",
            gap: 0,
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            alignContent: "start",
          }}
        >
          <div style={SECTION_HEADER_STYLE}>Admin</div>
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} style={navLinkStyle}>
              <span>{item.label}</span>
            </NavLink>
          ))}

          <div style={{ ...SECTION_HEADER_STYLE, marginTop: 8 }}>Account</div>
          <button
            type="button"
            onClick={handleSignOut}
            style={{
              font: "inherit",
              fontSize: 13,
              padding: "9px 16px",
              border: "none",
              borderLeft: "3px solid transparent",
              borderRadius: 0,
              background: "transparent",
              color: "var(--text)",
              cursor: "pointer",
              textAlign: "left",
              fontWeight: 400,
            }}
          >
            Sign out
          </button>
        </nav>
      </aside>

      <section style={{ minWidth: 0 }}>
        <Outlet />
      </section>
    </div>
  );
}
